import type { IFolderKnowledgeFile } from "@frontend-shared/types";
import {
  EFileUploadStatus,
  EKnowledgeProcessingStatus,
} from "@frontend-shared/types";
import { FileText, Loader2, Trash, X } from "lucide-react";
import React, { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useDeleteFolderKnowledgeFile,
  useGetFolderKnowledgeFiles,
  useUploadFolderKnowledgeFile,
} from "@/api/knowledge";
import { DeleteConfirmationDialog } from "@/components/DeleteConfirmationDialog";
import { Button } from "@/components/ui/button";
import { KnowledgeFileUpload } from "@/pages/knowledge/components/KnowledgeFileUpload";

interface IFolderKnowledgePanelProps {
  folderUuid: string;
}

const FolderKnowledgePanelInner: React.FC<IFolderKnowledgePanelProps> = ({
  folderUuid,
}) => {
  const { t } = useTranslation("sidebar");

  const { data: files, isLoading } = useGetFolderKnowledgeFiles({ folderUuid });
  const { uploads, uploadFile, removeUpload } =
    useUploadFolderKnowledgeFile(folderUuid);
  const { mutateAsync: deleteFile, isPending: isDeleting } =
    useDeleteFolderKnowledgeFile(folderUuid);

  const [fileToDelete, setFileToDelete] = useState<IFolderKnowledgeFile | null>(
    null,
  );

  const hasActiveUploads = useMemo(
    () => uploads.some((entry) => entry.status === EFileUploadStatus.UPLOADING),
    [uploads],
  );

  // NOTE: Only surface in-flight and failed uploads inline; completed uploads
  // appear in the refetched file list below.
  const pendingUploads = useMemo(
    () =>
      uploads.filter(
        (entry) =>
          entry.status === EFileUploadStatus.UPLOADING ||
          entry.status === EFileUploadStatus.ERROR,
      ),
    [uploads],
  );

  const handleUpload = useCallback(
    (selectedFiles: File[]): void => {
      for (const file of selectedFiles) {
        uploadFile(file);
      }
    },
    [uploadFile],
  );

  const handleConfirmDelete = useCallback((): void => {
    if (!fileToDelete) {
      return;
    }
    deleteFile(fileToDelete.uuid)
      .then(() => setFileToDelete(null))
      .catch(() => {
        // NOTE: Keep the confirmation open so the user can retry.
      });
  }, [deleteFile, fileToDelete]);

  const getStatusLabel = useCallback(
    (file: IFolderKnowledgeFile): string | null => {
      if (file.processingStatus === EKnowledgeProcessingStatus.FAILED) {
        return t("chat_folder_list.folder_settings.file_failed");
      }
      if (
        file.processingStatus === EKnowledgeProcessingStatus.PENDING ||
        file.processingStatus === EKnowledgeProcessingStatus.PROCESSING
      ) {
        return t("chat_folder_list.folder_settings.file_processing");
      }
      return null;
    },
    [t],
  );

  return (
    <div className="space-y-3" data-testid="folder-knowledge-panel">
      <p className="text-sm font-medium">
        {t("chat_folder_list.folder_settings.knowledge_title")}
      </p>

      <KnowledgeFileUpload
        onUpload={handleUpload}
        isUploading={hasActiveUploads}
      />

      {pendingUploads.map((entry) => (
        <div
          key={entry.id}
          className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm"
          data-testid={`folder-knowledge-upload-${entry.id}`}
        >
          {entry.status === EFileUploadStatus.UPLOADING ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
          ) : (
            <X className="h-4 w-4 shrink-0 text-destructive" />
          )}
          <span className="flex-1 truncate">{entry.fileName}</span>
          {entry.status === EFileUploadStatus.UPLOADING ? (
            <span className="text-xs text-muted-foreground">
              {entry.progress}%
            </span>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              onClick={() => removeUpload(entry.id)}
              data-testid={`folder-knowledge-upload-dismiss-${entry.id}`}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      ))}

      {isLoading ? (
        <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
          {t("chat_folder_list.folder_settings.knowledge_loading")}
        </div>
      ) : files && files.length > 0 ? (
        <ul className="space-y-1">
          {files.map((file) => {
            const statusLabel = getStatusLabel(file);
            return (
              <li
                key={file.uuid}
                className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm"
                data-testid={`folder-knowledge-file-${file.uuid}`}
              >
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="flex-1 truncate">{file.name}</span>
                {statusLabel && (
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {statusLabel}
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0"
                  onClick={() => setFileToDelete(file)}
                  data-testid={`folder-knowledge-file-delete-${file.uuid}`}
                >
                  <Trash className="h-4 w-4" />
                </Button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="py-4 text-center text-sm text-muted-foreground">
          {t("chat_folder_list.folder_settings.knowledge_empty")}
        </p>
      )}

      <DeleteConfirmationDialog
        open={fileToDelete !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setFileToDelete(null);
          }
        }}
        isDeleting={isDeleting}
        testIdPrefix="folder-knowledge-file"
        content={{
          step1: {
            title: t("chat_folder_list.folder_settings.delete_file_title"),
            description: t(
              "chat_folder_list.folder_settings.delete_file_description",
              { name: fileToDelete?.name ?? "" },
            ),
          },
        }}
        handlers={{
          onStep1Submit: handleConfirmDelete,
        }}
      />
    </div>
  );
};

export const FolderKnowledgePanel = memo(FolderKnowledgePanelInner);
